import React, { useContext, useRef, useState, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { FirebaseContext } from "../contexts/FirebaseContext";
import DropdownMenu from "./DropdownMenu";
import { ReactComponent as PlayIcon } from "../assets/icon_play.svg";
import { ReactComponent as StopIcon } from "../assets/icon_stop.svg";
import moreIcon from "../assets/icon_more.svg";
import editIcon from "../assets/icon_edit.svg";
import deleteIcon from "../assets/icon_delete.svg";

export const ACTION_DND_TYPE = "ACTION";

// A single action row in a task's Actions tab. Mirrors Android's
// ReadOnlyActionItem, left to right: drag grip, checkbox, the `actionLabel`,
// then on the right a tight cluster of [human play/stop] [AI play/stop]
// [overflow …]. The AI button only appears when AI tracking is enabled.
const ActionItem = ({
  action,
  index,
  moveAction,
  onDropEnd,
  onConvertToTask,
  onDelete,
  isTracked = false,
  isAiTracked = false,
  showAiTracking = false,
  onTrackToggle = () => {},
  onAiTrackToggle = () => {},
  onCheckFinalize = () => {},
}) => {
  const { updateAction } = useContext(FirebaseContext);
  const rowRef = useRef(null);
  const handleRef = useRef(null);
  const inputRef = useRef(null);

  // Inline-edit the action label: tapping the text swaps it for a focused
  // input (cursor shown), Enter/blur commits, Escape cancels. Mirrors Android,
  // where the action label is directly editable.
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(action.actionLabel || "");

  useEffect(() => {
    // Keep the draft in sync when the row isn't being edited (e.g. a sync
    // update lands), so we don't clobber a fresh remote value with stale text.
    if (!editing) setDraftLabel(action.actionLabel || "");
  }, [action.actionLabel, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Place the cursor at the end of the existing text.
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  const commitLabel = () => {
    setEditing(false);
    const next = draftLabel.trim();
    if (next === (action.actionLabel || "").trim()) return; // no change
    updateAction(action.id, { actionLabel: next });
  };

  const cancelEdit = () => {
    setDraftLabel(action.actionLabel || "");
    setEditing(false);
  };

  const [{ isDragging }, drag, preview] = useDrag({
    type: ACTION_DND_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    // Persist the final order once the drag completes.
    end: () => onDropEnd(),
  });

  const [, drop] = useDrop({
    accept: ACTION_DND_TYPE,
    hover: (item) => {
      if (item.index === index) return;
      moveAction(item.index, index); // optimistic local reorder
      item.index = index; // keep the dragged item's index in sync
    },
  });

  // The whole row is the drop target and the drag preview; only the grip
  // handle initiates a drag, so the menu/checkbox clicks behave normally.
  drop(preview(rowRef));
  drag(handleRef);

  const handleToggle = async () => {
    const checked = !action.checkState;
    // Checking an action finalizes any timer attributed to it FIRST (awaited),
    // so its elapsed time is banked into a record before the box flips — the
    // iOS/Android behavior. onCheckFinalize reads the LIVE task state, so it's
    // correct even if this row's tracking flags lag a just-started timer (the
    // bug where checking stopped tracking without generating a record).
    if (checked) {
      await onCheckFinalize();
    }
    updateAction(action.id, { checkState: checked });
  };

  const menuItems = [
    {
      icon: editIcon,
      label: "Convert to task",
      onClick: () => onConvertToTask(action),
    },
    {
      icon: deleteIcon,
      label: "Delete",
      onClick: () => onDelete(action),
    },
  ];

  return (
    <div ref={rowRef} style={{ ...styles.container, opacity: isDragging ? 0.4 : 1 }}>
      <span ref={handleRef} style={styles.handle} title="Drag to reorder">
        ⠿
      </span>
      <input
        type="checkbox"
        checked={!!action.checkState}
        onChange={handleToggle}
        style={styles.checkbox}
      />
      {editing ? (
        <input
          ref={inputRef}
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitLabel();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancelEdit();
            }
          }}
          style={styles.labelInput}
        />
      ) : (
        <p
          onClick={() => setEditing(true)}
          title="Click to edit"
          style={{
            ...styles.label,
            ...(action.checkState ? styles.labelChecked : {}),
          }}
        >
          {action.actionLabel || "(untitled action)"}
        </p>
      )}

      {/* Right cluster: [human play/stop] [AI play/stop] [overflow …] */}
      <button
        type="button"
        onClick={onTrackToggle}
        style={styles.trackBtn}
        aria-label={isTracked ? "Stop tracking action" : "Track action"}
      >
        {isTracked ? (
          <StopIcon className="track-icon-danger" style={styles.trackIcon} />
        ) : (
          <PlayIcon className="track-icon-human" style={styles.trackIcon} />
        )}
      </button>
      {showAiTracking && (
        <button
          type="button"
          onClick={onAiTrackToggle}
          style={styles.trackBtn}
          aria-label={isAiTracked ? "Stop AI tracking action" : "Track action with AI"}
        >
          {isAiTracked ? (
            <StopIcon className="track-icon-danger" style={styles.trackIcon} />
          ) : (
            <PlayIcon className="track-icon-ai" style={styles.trackIcon} />
          )}
        </button>
      )}
      <DropdownMenu icon={moreIcon} menuItems={menuItems} alignRight />
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    marginBottom: "8px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--card)",
  },
  handle: {
    marginRight: "12px",
    fontSize: "20px",
    color: "var(--text-faint)",
    cursor: "grab",
    userSelect: "none",
    flexShrink: 0,
  },
  checkbox: {
    width: "18px",
    height: "18px",
    marginRight: "14px",
    cursor: "pointer",
    flexShrink: 0,
  },
  label: {
    flex: 1,
    minWidth: 0,
    fontSize: "16px",
    color: "var(--text)",
    margin: 0,
    marginRight: "12px",
    cursor: "text", // signal the label is editable on click
  },
  labelInput: {
    flex: 1,
    minWidth: 0,
    fontSize: "16px",
    color: "var(--text)",
    margin: 0,
    marginRight: "12px",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid var(--border)",
    outline: "none",
    padding: 0,
    fontFamily: "inherit",
  },
  trackBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  trackIcon: { width: "20px", height: "20px" },
  labelChecked: {
    textDecoration: "line-through",
    color: "var(--text-faint)",
  },
};

export default ActionItem;
